// Audio playback via WebCodecs AudioDecoder (Opus) + Web Audio scheduling.
// RustDesk sends Misc.audio_format {sample_rate, channels} once, then a stream
// of Message.audio_frame {data = one Opus packet}. We decode each packet to PCM
// and schedule it on a running timeline for gapless playback.

export class AudioPlayer {
  private ctx: AudioContext | undefined;
  private decoder: AudioDecoder | undefined;
  private nextTime = 0; // scheduled playback cursor (ctx time)
  private ts = 0; // monotonic decode timestamp (µs)
  private sampleRate = 48000;
  private channels = 2;
  private muted = false;

  static isSupported(): boolean {
    return (
      typeof (globalThis as any).AudioDecoder !== "undefined" &&
      typeof (globalThis as any).AudioContext !== "undefined"
    );
  }

  constructor() {
    // Created during the Connect click (a user gesture) so the context starts
    // "running" rather than "suspended".
    try {
      if (AudioPlayer.isSupported()) this.ctx = new AudioContext();
    } catch (e) {
      console.error("[audio] AudioContext unavailable", e);
    }
  }

  /** (Re)configure the Opus decoder for a new stream format. */
  init(sampleRate: number, channels: number) {
    if (!this.ctx) return;
    this.sampleRate = sampleRate || 48000;
    this.channels = channels || 2;
    this.ctx.resume?.().catch(() => {});
    try {
      this.decoder?.close();
    } catch {
      /* ignore */
    }
    this.nextTime = 0;
    this.ts = 0;
    this.decoder = new AudioDecoder({
      output: (data) => this.play(data),
      error: (e) => console.error("[audio] decode error", e),
    });
    this.decoder.configure({
      codec: "opus",
      sampleRate: this.sampleRate,
      numberOfChannels: this.channels,
    });
    console.log(`[audio] opus ${this.sampleRate}Hz x${this.channels}`);
  }

  feed(data: Uint8Array) {
    const dec = this.decoder;
    if (!dec || dec.state !== "configured" || this.muted) return;
    try {
      dec.decode(new EncodedAudioChunk({ type: "key", timestamp: this.ts, data }));
      this.ts += 20000; // ~20ms/packet; monotonic. Real duration comes from AudioData.
    } catch (e) {
      console.error("[audio] feed error", e);
    }
  }

  private play(data: AudioData) {
    try {
      const ctx = this.ctx;
      if (!ctx || this.muted) return;
      const frames = data.numberOfFrames;
      const ch = data.numberOfChannels;
      const buf = ctx.createBuffer(ch, frames, data.sampleRate);
      for (let c = 0; c < ch; c++) {
        const tmp = new Float32Array(frames);
        data.copyTo(tmp, { planeIndex: c, format: "f32-planar" });
        buf.copyToChannel(tmp, c);
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      const now = ctx.currentTime;
      // Keep a small jitter buffer; if we've fallen behind, resync.
      if (this.nextTime < now + 0.02) this.nextTime = now + 0.05;
      src.start(this.nextTime);
      this.nextTime += buf.duration;
    } catch (e) {
      console.error("[audio] play error", e);
    } finally {
      data.close();
    }
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (m) this.nextTime = 0;
  }

  resume() {
    this.ctx?.resume?.().catch(() => {});
  }

  close() {
    try {
      this.decoder?.close();
    } catch {
      /* ignore */
    }
    this.decoder = undefined;
    try {
      this.ctx?.close?.();
    } catch {
      /* ignore */
    }
    this.ctx = undefined;
  }
}
